<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcPaymentInvoices',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'recalculate']);

  const props = defineProps({
    ...userComponentProps,
    payment: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const createInvoicePaymentData = (...args) => {
    const [data] = args;
    const unpaidAmount =
      data.unpaid ?? data.outstanding_amounts ?? data.total ?? 0;
    return {
      invoice_id: data.id,
      invoice_number: data.invoice_number,
      currency: data.currency,
      amount: unpaidAmount,
      exchange_rate: 1,
      client: data.client,
      invoice: data,
    };
  };

  const messages = ref({
    grossAmount: $fn.value.pgettext('Invoice', 'Gross amount'),
    paidAmount: $fn.value.pgettext('Invoice', 'Payments'),
  });
  const ux = ref([]);
  const b_5962862420439144_paging = computed(() => false);
  const b_5962862420439144_sortable = computed(() => false);
  const b_5962862420439144_columns = computed(() => [
    {
      name: 'invoice_number',
      width: '240px',
      header: $fn.value.pgettext('InvoicePayment', 'Invoice'),
      readonly: true,
      textAlign: 'left',
      formatter: {
        name: 'template',
        value:
          '<div>\n\
  <UcInvoiceBadge :value="row.invoice"></UcInvoiceBadge>\n\
\n\
  <div>\n\
    {{ date(row.invoice.invoice_date) }}\n\
  </div>\n\
\n\
  <div class="d-flex gap-2">\n\
    <ItpText>{{ globs.grossAmount }}:</ItpText>\n\
    <strong>{{ number(row.invoice.total_amount) }} {{ row.invoice.currency }}</strong><br/>\n\
  </div>\n\
\n\
  <div v-if="row.invoice.total_payment" class="d-flex gap-2">\n\
    <ItpText>{{ globs.paidAmount }}:</ItpText>\n\
    <strong>{{ number(row.invoice.total_payment) }} {{ row.invoice.currency }}</strong><br/>\n\
  </div>\n\
  <UcCompanyMiniCard :item="row.invoice.client" no-link></UcCompanyMiniCard>\n\
</div>\n\
',
      },
    },
    {
      name: 'amount',
      header: $fn.value.pgettext('InvoicePayment', 'Amount'),
      type: 'number',
      width: '110px',
      textAlign: 'right',
      required: true,
    },
    {
      name: 'exchange_rate',
      width: '70px',
      header: $fn.value.pgettext('InvoicePayment', 'Exch. Rate'),
      type: 'number',
      textAlign: 'right',
      required: true,
      noFooterTotals: true,
    },
    {
      name: 'notes',
      width: '210px',
      header: $fn.value.pgettext('InvoicePayment', 'Notes'),
    },
  ]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
    b_5962862420439144_paging: b_5962862420439144_paging.value,
    b_5962862420439144_sortable: b_5962862420439144_sortable.value,
    b_5962862420439144_columns: b_5962862420439144_columns.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'ux' },
      { name: 'b_5962862420439144_paging', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_sortable', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_columns', nodeId: '5962862420439144' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      messages,
      ux,
      b_5962862420439144_paging,
      b_5962862420439144_sortable,
      b_5962862420439144_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const invoiceTotals = computed(() => {
    return _.isEmpty(props.payment.invoices && props.payment.invoices.data)
      ? null
      : _.chain(props.payment.invoices.data)
          .reduce(
            (sum, v) => (v.amount == null ? sum : (sum.add(v.amount), sum)),
            $fn.value.numeral(0)
          )
          .value();
  });
  const invoiceTotalsDiff = computed(() => {
    return invoiceTotals.value != null
      ? $fn.value
          .numeral(props.payment.amount)
          .difference(invoiceTotals.value.value())
      : null;
  });

  watch(
    () => props.payment,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('payment');
    }
  );

  const act_3343864469950044_script_fdebixku1_value_callable = async (
    $event
  ) => {
    return (
      !props.payment.invoices && _.set(props.payment, 'invoices', { data: [] }),
      !_.isArray(props.payment.invoices.data) &&
        (props.payment.invoices.data = []),
      _.chain($event.data.modalResult.value)
        .castArray()
        .compact()
        .map(createInvoicePaymentData)
        .filter(
          (p) =>
            p &&
            p.invoice_id &&
            !_.some(props.payment.invoices.data, { invoice_id: p.invoice_id })
        )
        .each((p) => props.payment.invoices.data.push(p))
        .value()
    );
  };
  const act_3343864469950044_script_fdebixku1_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3343864469950044_script_fdebixku1_value_callable($event);
      },
    };
  };
  const inst_evh_3343864469950044_close_qequ92r0p_getWhen = async ($event) => {
    return $event.data.modalResult.ok && $event.data.modalResult.value;
  };
  const act_3343864469950044_emit_ngglu285h_getActionArgs = async ($event) => {
    return {
      event: 'recalculate',
    };
  };
  const inst_evh_3343864469950044_close_io3chcb9d_getWhen = async ($event) => {
    return $event.data.modalResult.ok;
  };
  const act_3343864469950044_showModal_v5jgznksp_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'InvoiceBrowser',
      props: {
        size: 'full-screen',
        company_id: props.payment.account && props.payment.account.owner.id,
        client_id: props.payment.partner && props.payment.partner.id,
        multiselect: true,
      },
    };
  };
  const act_5962862420439144_emit_4d2wpv87p_getActionArgs = async ($event) => {
    return {
      event: 'recalculate',
      value: $event.data.result,
    };
  };
  const act_5962862420439144_script_8sfs1g7hd_value_callable = async (
    $event
  ) => {
    return _.remove(props.payment.invoices.data, {
      invoice_id: $event.data.row.invoice_id,
    });
  };
  const act_5962862420439144_script_8sfs1g7hd_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_8sfs1g7hd_value_callable($event);
      },
    };
  };
  const act_5962862420439144_emit_h0icsjeuc_getActionArgs = async ($event) => {
    return {
      event: 'recalculate',
    };
  };

  const act_7315092382398562_reloadComponentData_4y0mkyjwd =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_1jcifd5lr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_4y0mkyjwd,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_1jcifd5lr],
    event: 'reload',
  });
  const act_3343864469950044_script_fdebixku1 = new actionTypes.ScriptAction({
    actionArgs: act_3343864469950044_script_fdebixku1_getActionArgs,
  });
  const inst_evh_3343864469950044_close_qequ92r0p =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_script_fdebixku1,
      event: 'close',
      when: inst_evh_3343864469950044_close_qequ92r0p_getWhen,
      displayName: 'script',
    });
  const act_3343864469950044_emit_ngglu285h = new actionTypes.EmitAction({
    actionArgs: act_3343864469950044_emit_ngglu285h_getActionArgs,
  });
  const inst_evh_3343864469950044_close_io3chcb9d =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_emit_ngglu285h,
      event: 'close',
      when: inst_evh_3343864469950044_close_io3chcb9d_getWhen,
      displayName: 'emit',
    });
  const act_3343864469950044_showModal_v5jgznksp =
    new actionTypes.ShowModalAction({
      actionArgs: act_3343864469950044_showModal_v5jgznksp_getActionArgs,
      displayName: 'addInvoice',
      events: [
        inst_evh_3343864469950044_close_qequ92r0p,
        inst_evh_3343864469950044_close_io3chcb9d,
      ],
    });
  const inst_evh_3343864469950044_click_zh24hadrh =
    new actionTypes.EventHandlerImpl({
      action: act_3343864469950044_showModal_v5jgznksp,
      event: 'click',
      displayName: 'addInvoice',
    });
  const inst_evh_3343864469950044_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3343864469950044_click_zh24hadrh],
    event: 'click',
  });
  const act_5962862420439144_emit_4d2wpv87p = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_4d2wpv87p_getActionArgs,
  });
  const inst_evh_5962862420439144_saveCompleted_72t6od5qp =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_4d2wpv87p,
      event: 'save-completed',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_saveCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_saveCompleted_72t6od5qp],
      event: 'save-completed',
    });
  const act_5962862420439144_script_8sfs1g7hd = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_8sfs1g7hd_getActionArgs,
  });
  const inst_evh_5962862420439144_delete_c3pplbd8a =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_8sfs1g7hd,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_delete_c3pplbd8a],
    event: 'delete',
  });
  const act_5962862420439144_emit_h0icsjeuc = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_h0icsjeuc_getActionArgs,
  });
  const inst_evh_5962862420439144_deleteCompleted_4ehmd1fn8 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_h0icsjeuc,
      event: 'delete-completed',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_deleteCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_deleteCompleted_4ehmd1fn8],
      event: 'delete-completed',
    });

  const actions = {
    inst_evh_7315092382398562_reload_1jcifd5lr,
    inst_evh_7315092382398562_reload,
    inst_evh_3343864469950044_close_qequ92r0p,
    inst_evh_3343864469950044_close_io3chcb9d,
    inst_evh_3343864469950044_click_zh24hadrh,
    addInvoice: inst_evh_3343864469950044_click_zh24hadrh,
    inst_evh_3343864469950044_click,
    inst_evh_5962862420439144_saveCompleted_72t6od5qp,
    inst_evh_5962862420439144_saveCompleted,
    inst_evh_5962862420439144_delete_c3pplbd8a,
    inst_evh_5962862420439144_delete,
    inst_evh_5962862420439144_deleteCompleted_4ehmd1fn8,
    inst_evh_5962862420439144_deleteCompleted,
  };

  const evh_7315092382398562_reload_1jcifd5lr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_1jcifd5lr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_close_qequ92r0p = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_close_qequ92r0p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_close_io3chcb9d = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_close_io3chcb9d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_click_zh24hadrh = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_click_zh24hadrh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3343864469950044_click = async (event, scope) => {
    return await actions.inst_evh_3343864469950044_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_saveCompleted_72t6od5qp = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted_72t6od5qp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_saveCompleted = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete_c3pplbd8a = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete_c3pplbd8a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_deleteCompleted_4ehmd1fn8 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_deleteCompleted_4ehmd1fn8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_deleteCompleted = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_deleteCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox alignItems="center" class="mb-2">
      <strong>
        {{ pgettext('Payment', 'Invoices') }}
      </strong>
      <ItpButton
        :icon="$config.addIcon"
        :text="pgettext('Payment', 'Add invoice...')"
        size="sm"
        variant="light"
        class="mx-4"
        v-if="!payment.isReadonly"
        @click="evh_3343864469950044_click($event, {})"
      >
      </ItpButton>
      <div
        :title="pgettext('Payment', 'Invoice amount mismatch')"
        class="pb-2 text-nowrap"
        v-if="invoiceTotalsDiff"
      >
        <Icon icon="emojione-v1:warning"> </Icon>
        <ItpText
          :text="
            pgettext(
              'payment',
              'Mismatch between the amount of payment and invoices.'
            )
          "
          class="text-wrap"
        >
        </ItpText>
      </div>
    </ItpBox>
    <ItpDataTable
      rowKeyField="invoice_id"
      tableId="paymentInvoices"
      :paging="b_5962862420439144_paging"
      :editable="!payment.isReadonly"
      :sortable="b_5962862420439144_sortable"
      :globs="messages"
      :value="payment.invoices.data"
      no-create-row
      no-confirm-delete-row
      footer-totals
      :columns="b_5962862420439144_columns"
      v-if="payment.invoices.data"
      @save-completed="evh_5962862420439144_saveCompleted($event, {})"
      @delete="evh_5962862420439144_delete($event, {})"
      @delete-completed="evh_5962862420439144_deleteCompleted($event, {})"
    >
    </ItpDataTable>
  </ItpUsercontrol>
</template>
